import { formatEuro } from './sf-money.js';
import { deliveryDeadline, shortDate } from './sf-delivery.js';

export const NO_NAME = 'Weiß ich noch nicht';

export function sanitizeShopName(input) {
  return (input || '').replace(/[<>]/g, '').slice(0, 30);
}

export function totalCents(baseCents, extraCents = []) {
  return baseCents + extraCents.reduce((sum, cents) => sum + cents, 0);
}

export function buyLabel(niche, totalText) {
  if (!niche || niche === 'Wählt ihr für mich') return `Shop für ${totalText} bestellen`;
  return `${niche}-Shop für ${totalText} bestellen`;
}

export function buildCartPayload({ variantId, niche, shopName, whatsapp, consentAt, extraVariantIds = [] }) {
  if (!variantId) throw new Error('variant_missing');
  if (!consentAt) throw new Error('consent_missing');
  const name = (shopName || '').trim() || NO_NAME;
  const properties = { Nische: niche, 'Shop-Name': name, 'Sofortige Ausführung verlangt': consentAt };
  const phone = (whatsapp || '').trim();
  if (phone) properties.WhatsApp = phone;
  return {
    items: [
      { id: Number(variantId), quantity: 1, properties },
      ...extraVariantIds.map((id) => ({ id: Number(id), quantity: 1, properties: { 'Für Shop': name } })),
    ],
  };
}

if (typeof customElements !== 'undefined' && !customElements.get('sf-configurator')) {
  customElements.define('sf-configurator', class extends HTMLElement {
    connectedCallback() {
      this.preselectFromUrl();
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => {
        if (event.target.name === 'shop_name') this.updateName();
      });
      this.querySelectorAll('[data-sf-buy]').forEach((button) => button.addEventListener('click', () => this.buy()));
      this.querySelectorAll('[data-sf-thumb]').forEach((thumb) => thumb.addEventListener('click', () => this.showDemo(thumb.dataset.desktop, thumb.dataset.mobile)));
      const now = new Date();
      this.querySelector('[data-sf-today]').textContent = shortDate(now);
      this.querySelector('[data-sf-handover]').textContent = `bis ${shortDate(deliveryDeadline(now))}`;
      this.update();
    }

    preselectFromUrl() {
      const wanted = new URLSearchParams(window.location.search).get('nische');
      if (!wanted) return;
      const match = [...this.querySelectorAll('input[name="niche"]')].find((input) => input.value === wanted);
      if (match) match.checked = true;
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-sf-bundle]') && target.checked) {
        this.querySelectorAll('[data-sf-single]').forEach((single) => { single.checked = false; });
      }
      if (target.matches('[data-sf-single]') && target.checked) {
        const bundle = this.querySelector('[data-sf-bundle]');
        if (bundle) bundle.checked = false;
      }
      if (target.name === 'niche') {
        this.showDemo(target.dataset.desktop, target.dataset.mobile);
      }
      this.update();
    }

    selectedNiche() {
      return this.querySelector('input[name="niche"]:checked');
    }

    selectedExtras() {
      return [...this.querySelectorAll('[data-extra-variant-id]:checked')];
    }

    showDemo(desktop, mobile) {
      const devices = this.querySelector('[data-sf-devices]');
      const desktopImg = this.querySelector('[data-sf-devices-desktop]');
      if (!devices || !desktopImg || desktopImg.src === desktop) return;
      devices.classList.add('is-switching');
      setTimeout(() => {
        desktopImg.src = desktop;
        this.querySelector('[data-sf-devices-mobile]').src = mobile;
        devices.classList.remove('is-switching');
      }, 250);
      this.querySelectorAll('[data-sf-thumb]').forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.desktop === desktop));
    }

    updateName() {
      const name = sanitizeShopName(this.querySelector('input[name="shop_name"]').value).trim();
      const chip = this.querySelector('[data-sf-name-chip]');
      if (!chip) return;
      chip.hidden = !name;
      this.querySelector('[data-sf-name-chip-text]').textContent = name;
    }

    update() {
      const extras = this.selectedExtras();
      const cents = totalCents(Number(this.dataset.baseCents), extras.map((el) => Number(el.dataset.extraCents)));
      const total = formatEuro(cents);
      const niche = this.selectedNiche()?.value ?? '';
      this.querySelectorAll('[data-sf-total]').forEach((el) => { el.textContent = total; });
      this.querySelectorAll('[data-sf-niche-value]').forEach((el) => { el.textContent = niche; });
      this.querySelector('[data-sf-buy-label]').textContent = buyLabel(niche, total);
      const lines = this.querySelector('[data-sf-lines]');
      lines.querySelectorAll('[data-sf-extra-line]').forEach((line) => line.remove());
      extras.forEach((el) => {
        const line = document.createElement('div');
        line.className = 'sf-order__line';
        line.dataset.sfExtraLine = '';
        const name = document.createElement('span');
        name.textContent = el.dataset.extraName;
        const price = document.createElement('span');
        price.textContent = formatEuro(Number(el.dataset.extraCents));
        line.append(name, price);
        lines.append(line);
      });
      if (this.querySelector('input[name="consent"]').checked) {
        this.querySelector('[data-sf-consent-error]').hidden = true;
        this.querySelector('.sf-consent').classList.remove('is-invalid');
      }
      this.updateName();
    }

    async buy() {
      const consent = this.querySelector('input[name="consent"]');
      const consentError = this.querySelector('[data-sf-consent-error]');
      const error = this.querySelector('[data-sf-error]');
      error.hidden = true;
      if (!consent.checked) {
        consentError.hidden = false;
        consent.closest('.sf-consent').classList.add('is-invalid');
        consent.scrollIntoView({ behavior: 'smooth', block: 'center' });
        consent.focus({ preventScroll: true });
        return;
      }
      const niche = this.selectedNiche();
      const payload = buildCartPayload({
        variantId: niche.dataset.variantId,
        niche: niche.value,
        shopName: sanitizeShopName(this.querySelector('input[name="shop_name"]').value),
        whatsapp: this.querySelector('input[name="whatsapp"]').value,
        consentAt: new Date().toISOString(),
        extraVariantIds: this.selectedExtras().map((el) => el.dataset.extraVariantId),
      });
      const root = window.Shopify?.routes?.root || '/';
      const buttons = this.querySelectorAll('[data-sf-buy]');
      buttons.forEach((button) => { button.disabled = true; });
      try {
        await fetch(`${root}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(`cart_add_${response.status}`);
        window.location.href = `${root}checkout`;
      } catch (err) {
        console.error(err);
        error.hidden = false;
        buttons.forEach((button) => { button.disabled = false; });
      }
    }
  });
}
